import { DeleteOutlined } from '@ant-design/icons';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Alert,
  Button,
  Card,
  Empty,
  Flex,
  Input,
  Modal,
  Popconfirm,
  Segmented,
  Select,
  Typography,
} from 'antd';
import { useRef, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import { ApiError } from '../../lib/api-client';
import { useDebounced } from '../../lib/use-debounced';
import {
  listAccessories,
  listBikeModels,
  listBikeVariants,
  replaceLines,
  type AccessoryOption,
  type BikeVariant,
  type Quotation,
  type QuotationLine,
  type QuotationLineInput,
} from './api';
import { formatMoney, isPositive } from '../../lib/money';
import {
  FEE_TYPES,
  toLineInputs,
  withAccessory,
  withBike,
  hasUnkeyed,
  isUnkeyed,
  priceChanges,
  sameDecimal,
  withDiscount,
  withFee,
  withoutLine,
  withQty,
  type FeeType,
  type PriceChange,
} from './quoteDraft';

const { Text, Title } = Typography;

/** Mutation key of the editor's save, so other controls can wait for it. */
export function quotationLinesKey(uid: string): readonly unknown[] {
  return ['quotation-lines', uid];
}

const DECIMAL = /^\d+(\.\d{1,2})?$/;
const QTY = /^\d+(\.\d{1,3})?$/;

function fieldLines(fields: unknown): string[] {
  if (typeof fields === 'string') return [fields];
  if (Array.isArray(fields)) {
    return fields.flatMap((f): string[] => {
      if (typeof f === 'string') return [f];
      if (typeof f === 'object' && f !== null) {
        const rec = f as Record<string, unknown>;
        const name = typeof rec['field'] === 'string' ? `${rec['field']}: ` : '';
        return typeof rec['message'] === 'string' ? [`${name}${rec['message']}`] : [];
      }
      return [];
    });
  }
  if (typeof fields === 'object' && fields !== null) {
    return Object.entries(fields).flatMap(([key, v]) => fieldLines(v).map((line) => `${key}: ${line}`));
  }
  return [];
}

/** A 422 is one sentence; a 400 is a message plus one line per field. */
function errorText(e: unknown, fallback: string): string {
  if (!(e instanceof ApiError)) return fallback;
  if (e.status !== 400) return e.message;
  return [e.message, ...fieldLines(e.details['fields'])].join('\n');
}

/** A picker option as two lines: what it is, then its price, so neither is cut off. */
function TwoLineOption({ description, price }: { description: string; price: string }): ReactNode {
  return (
    <Flex vertical style={{ whiteSpace: 'normal' }}>
      <span>{description}</span>
      <Text type="secondary">{price}</Text>
    </Flex>
  );
}

interface Props {
  quotation: Quotation;
  onSaved: () => void;
}

export function QuotationEditor({ quotation, onSaved }: Props): ReactNode {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [modal, modalHolder] = Modal.useModal();
  const [searchParams] = useSearchParams();
  const focusBike = searchParams.get('choose') === 'bike';

  const [modelTerm, setModelTerm] = useState('');
  const [modelUid, setModelUid] = useState<string | undefined>(undefined);
  const [mode, setMode] = useState<'part' | 'fee'>('part');
  const [partTerm, setPartTerm] = useState('');
  const [part, setPart] = useState<AccessoryOption | undefined>(undefined);
  const [feeType, setFeeType] = useState<FeeType>('INSURANCE');
  const [feeAmount, setFeeAmount] = useState('');
  const [feeNote, setFeeNote] = useState('');
  const [localError, setLocalError] = useState<string | null>(null);
  // A draft is re-priced from AutoCount on every save; these say when that moved a price.
  const [repriced, setRepriced] = useState<PriceChange[]>([]);
  // Bumped when an edit is refused so the inputs fall back to the server's value.
  const [resets, setResets] = useState(0);
  // One save at a time: every control is disabled while this is true, and the
  // ref closes the gap between a blur and the re-render that disables things.
  const busy = useRef(false);

  const debouncedModel = useDebounced(modelTerm.trim(), 300);
  const debouncedPart = useDebounced(partTerm.trim(), 300);

  const models = useQuery({
    queryKey: ['sales-bike-models', debouncedModel],
    queryFn: () => listBikeModels(debouncedModel),
  });
  const variants = useQuery({
    queryKey: ['sales-bike-variants', modelUid],
    queryFn: () => listBikeVariants(modelUid ?? ''),
    enabled: modelUid !== undefined,
  });
  const accessories = useQuery({
    queryKey: ['sales-accessories', debouncedPart],
    queryFn: () => listAccessories(debouncedPart),
    enabled: mode === 'part',
  });

  const save = useMutation({
    // QuotationDetailPage holds "Send to customer" while this key is in flight:
    // the tap on Send is the blur that saves a discount.
    mutationKey: quotationLinesKey(quotation.uid),
    mutationFn: (input: { lines: QuotationLineInput[]; previous: QuotationLine[] }) =>
      replaceLines(quotation.uid, input.lines),
    onSuccess: (saved, input) => {
      setRepriced(priceChanges(input.previous, saved.lines));
      // The PUT response is the authoritative new state: put it in the cache
      // before the guard releases, so the next edit never builds from stale lines.
      queryClient.setQueryData(['quotation', quotation.uid], saved);
      setLocalError(null);
      onSaved();
    },
    onError: () => {
      setResets((n) => n + 1);
    },
    onSettled: () => {
      busy.current = false;
    },
  });
  const locked = save.isPending;
  const currencySymbol = formatMoney('0', quotation.currency).split(' ')[0] ?? quotation.currency;

  const reject = (text: string): void => {
    setLocalError(text);
    setResets((n) => n + 1);
  };

  const hasBike = quotation.lines.some((l) => l.type === 'BIKE');
  const current = (): QuotationLineInput[] => toLineInputs(quotation.lines);
  const change = (lines: QuotationLineInput[]): void => {
    if (busy.current) return;
    if (hasUnkeyed(lines)) {
      reject(t('quotation.unkeyed'));
      return;
    }
    busy.current = true;
    setLocalError(null);
    save.mutate({ lines, previous: quotation.lines });
  };

  const pickVariant = (itemCode: string): void => {
    const variant: BikeVariant | undefined = variants.data?.find((v) => v.itemCode === itemCode);
    if (variant === undefined) return;
    const next = withBike(current(), variant);
    if (!hasBike) {
      change(next);
      return;
    }
    modal.confirm({
      title: t('quotation.replaceBikeTitle'),
      content: t('quotation.replaceBikeBody'),
      okText: t('quotation.replaceBike'),
      onOk: () => {
        change(next);
      },
    });
  };

  const addPart = (): void => {
    if (part === undefined) return;
    change(withAccessory(current(), part));
    setPart(undefined);
    setPartTerm('');
  };

  const addFee = (): void => {
    const amount = feeAmount.trim();
    if (!DECIMAL.test(amount) || !isPositive(amount)) {
      setLocalError(t('quotation.amountInvalid'));
      return;
    }
    change(withFee(current(), feeType, amount, feeNote));
    setFeeAmount('');
    setFeeNote('');
  };

  const saveDiscount = (index: number, raw: string, previous: string): void => {
    const value = raw.trim() === '' ? '0.00' : raw.trim();
    if (!DECIMAL.test(value)) {
      reject(t('quotation.amountInvalid'));
      return;
    }
    if (sameDecimal(value, previous)) return;
    change(withDiscount(current(), index, value));
  };

  const saveQty = (index: number, raw: string, previous: string): void => {
    const value = raw.trim();
    if (!QTY.test(value) || !isPositive(value)) {
      reject(t('quotation.qtyInvalid'));
      return;
    }
    if (sameDecimal(value, previous)) return;
    change(withQty(current(), index, value));
  };

  const errorMessage = localError ?? (save.isError ? errorText(save.error, t('quotation.saveFailed')) : null);
  // The line a refusal names (details.itemCode / details.skuUid), so it can be marked.
  const refusedBy = save.error instanceof ApiError ? save.error.details : {};
  const isRefusedLine = (line: QuotationLine): boolean =>
    (line.type === 'BIKE' &&
      line.itemCode !== null &&
      typeof refusedBy['itemCode'] === 'string' &&
      refusedBy['itemCode'].toUpperCase() === line.itemCode.toUpperCase()) ||
    (line.type === 'ACCESSORY' && line.skuUid !== null && refusedBy['skuUid'] === line.skuUid);

  return (
    <Flex vertical gap={16}>
      {modalHolder}
      {errorMessage !== null && (
        <Alert
          type="error"
          showIcon
          message={<span style={{ whiteSpace: 'pre-line' }}>{errorMessage}</span>}
        />
      )}
      {repriced.length > 0 && (
        <Alert
          type="info"
          showIcon
          closable
          onClose={() => {
            setRepriced([]);
          }}
          message={
            <Flex vertical gap={4}>
              {repriced.map((change) => (
                <span key={`${change.description}:${change.to}`}>
                  {t('quotation.priceChanged', {
                    item: change.description,
                    from: formatMoney(change.from, quotation.currency),
                    to: formatMoney(change.to, quotation.currency),
                  })}
                </span>
              ))}
            </Flex>
          }
        />
      )}

      <Card title={t('quotation.chooseBike')}>
        <Flex vertical gap={12}>
          <Select
            size="large"
            showSearch
            disabled={locked}
            autoFocus={focusBike}
            filterOption={false}
            placeholder={t('quotation.modelPlaceholder')}
            value={modelUid}
            loading={models.isFetching}
            onSearch={setModelTerm}
            onChange={(v: string) => {
              setModelUid(v);
            }}
            options={(models.data ?? []).map((m) => ({ value: m.uid, label: m.label }))}
            notFoundContent={t('quotation.noModels')}
          />
          {modelUid !== undefined && (
            <Select
              size="large"
              disabled={locked}
              placeholder={t('quotation.variantPlaceholder')}
              loading={variants.isFetching || locked}
              value={null}
              onChange={pickVariant}
              options={(variants.data ?? []).map((v) => ({
                value: v.itemCode,
                label: `${v.description} — ${formatMoney(v.listPrice, quotation.currency)}`,
                description: v.description,
                price: formatMoney(v.listPrice, quotation.currency),
              }))}
              // Two lines: on a phone a one-line option cuts off the price.
              optionRender={(option) => <TwoLineOption {...option.data} />}
              notFoundContent={t('quotation.noVariants')}
            />
          )}
        </Flex>
      </Card>

      <Card title={t('quotation.addExtra')}>
        <Flex vertical gap={12}>
          <Segmented
            size="large"
            block
            value={mode}
            onChange={(v) => {
              setMode(v === 'fee' ? 'fee' : 'part');
            }}
            options={[
              { value: 'part', label: t('quotation.part') },
              { value: 'fee', label: t('quotation.fee') },
            ]}
          />
          {mode === 'part' ? (
            <>
              <Select
                size="large"
                showSearch
                disabled={locked}
                filterOption={false}
                placeholder={t('quotation.partPlaceholder')}
                value={part?.uid}
                loading={accessories.isFetching}
                onSearch={setPartTerm}
                onChange={(uid: string) => {
                  setPart(accessories.data?.find((a) => a.uid === uid));
                }}
                options={(accessories.data ?? []).map((a) => ({
                  value: a.uid,
                  label: `${a.skuCode} · ${a.name} — ${formatMoney(a.sellPrice, quotation.currency)}`,
                  description: `${a.skuCode} · ${a.name}`,
                  price: formatMoney(a.sellPrice, quotation.currency),
                }))}
                optionRender={(option) => <TwoLineOption {...option.data} />}
                notFoundContent={t('quotation.noParts')}
              />
              <Button
                type="primary"
                size="large"
                disabled={part === undefined || locked}
                loading={locked}
                onClick={addPart}
              >
                {t('quotation.addPart')}
              </Button>
            </>
          ) : (
            <>
              <Select
                size="large"
                disabled={locked}
                value={feeType}
                onChange={(v: FeeType) => {
                  setFeeType(v);
                }}
                options={FEE_TYPES.map((f) => ({ value: f, label: t(`quotation.lineTypes.${f}`) }))}
              />
              <Input
                size="large"
                disabled={locked}
                inputMode="decimal"
                prefix={currencySymbol}
                placeholder={t('quotation.amountPlaceholder')}
                value={feeAmount}
                onChange={(e) => {
                  setFeeAmount(e.target.value);
                }}
                onPressEnter={addFee}
              />
              <Input
                size="large"
                disabled={locked}
                maxLength={120}
                placeholder={t('quotation.feeNotePlaceholder')}
                value={feeNote}
                onChange={(e) => {
                  setFeeNote(e.target.value);
                }}
                onPressEnter={addFee}
              />
              <Button type="primary" size="large" loading={locked} disabled={locked} onClick={addFee}>
                {t('quotation.addFee')}
              </Button>
            </>
          )}
        </Flex>
      </Card>

      <Card
        title={t('sales.lines')}
        extra={
          <Title level={5} style={{ margin: 0 }}>
            {formatMoney(quotation.grandTotal, quotation.currency)}
          </Title>
        }
      >
        {quotation.lines.length === 0 || !hasBike ? (
          <Empty description={t('quotation.empty')} image={Empty.PRESENTED_IMAGE_SIMPLE} />
        ) : null}
        <Flex vertical gap={16}>
          {quotation.lines.map((line, index) => {
            const priced = line.type === 'BIKE' || line.type === 'ACCESSORY';
            const unkeyed = isUnkeyed(
              toLineInputs([line])[0] ?? { type: line.type, description: '', qty: '1' },
            );
            return (
              <Flex key={line.uid} vertical gap={8}>
                <Flex justify="space-between" align="start" gap={8}>
                  <Flex vertical>
                    <Text strong>{line.description}</Text>
                    <Text type="secondary">{t(`quotation.lineTypes.${line.type}`)}</Text>
                  </Flex>
                  <Popconfirm
                    title={t('quotation.removeTitle', { name: line.description })}
                    description={t('quotation.removeBody')}
                    okText={t('quotation.remove')}
                    onConfirm={() => {
                      change(withoutLine(current(), index));
                    }}
                  >
                    <Button
                      danger
                      size="large"
                      icon={<DeleteOutlined />}
                      aria-label={t('quotation.remove')}
                      disabled={locked}
                    >
                      {t('quotation.remove')}
                    </Button>
                  </Popconfirm>
                </Flex>
                {save.isError && isRefusedLine(line) && (
                  <Alert type="error" showIcon message={t('quotation.lineRefused')} />
                )}
                {unkeyed && (
                  <Alert
                    type="warning"
                    showIcon
                    message={t(line.type === 'BIKE' ? 'quotation.unkeyedBike' : 'quotation.unkeyedPart')}
                  />
                )}
                {line.type === 'ACCESSORY' && (
                  <Input
                    key={`${line.uid}:${line.qty}:${String(resets)}:qty`}
                    size="large"
                    disabled={locked}
                    inputMode="decimal"
                    prefix={t('sales.fields.qty')}
                    defaultValue={line.qty}
                    onBlur={(e) => {
                      saveQty(index, e.target.value, line.qty);
                    }}
                  />
                )}
                <Flex justify="space-between" gap={8} wrap>
                  <Text type="secondary">
                    {priced ? t('quotation.priceFromAutoCount') : t('sales.fields.unitPrice')}
                  </Text>
                  <Text>{formatMoney(line.unitPrice, quotation.currency)}</Text>
                </Flex>
                <Input
                  key={`${line.uid}:${line.discountAmount}:${String(resets)}:discount`}
                  size="large"
                  disabled={locked}
                  inputMode="decimal"
                  prefix={t('sales.fields.discount')}
                  defaultValue={line.discountAmount}
                  onBlur={(e) => {
                    saveDiscount(index, e.target.value, line.discountAmount);
                  }}
                />
                <Text type="secondary">{t('quotation.discountBand')}</Text>
                <Flex justify="space-between">
                  <Text type="secondary">{t('sales.fields.lineTotal')}</Text>
                  <Text strong>{formatMoney(line.lineTotal, quotation.currency)}</Text>
                </Flex>
              </Flex>
            );
          })}
        </Flex>
      </Card>
    </Flex>
  );
}
